// Auto-detect environment based on current URL
const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
const API_BASE_URL = import.meta.env.VITE_API_URL || (isLocalDev 
  ? 'http://localhost:8000' 
  : 'https://dataraiils-backend.me-fd5.workers.dev')

console.log(`🌍 Environment: ${isLocalDev ? 'LOCAL DEVELOPMENT' : 'PRODUCTION'}`)
console.log(`📡 API Base URL: ${API_BASE_URL}`)
const __savedToken = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null
if (__savedToken) {
  // Seed token immediately on module load so first requests are authenticated
  try {
    // Avoid circular init timing by setting after instance is created (runtime below)
    ;(window as any).__seed_api_token__ = __savedToken
  } catch {}
}
console.log('🔐 Token (set?):', Boolean(__savedToken))

export interface Asset {
  id: string
  filename: string
  originalName: string
  size: number
  mimetype: string
  metadata: {
    campaign?: string
    type?: string
    dimensions?: string
    status?: 'pending' | 'processing' | 'completed' | 'flagged'
  }
  createdAt: string
  updatedAt: string
}

export interface WorkflowStep {
  id: string
  name: string
  status: 'pending' | 'in_progress' | 'completed' | 'failed'
  progress: number
  totalSteps: number
  assets: Asset[]
  createdAt: string
  updatedAt: string
}

class ApiService {
  private token: string | null = null

  setToken(token: string | null) {
    this.token = token
  }

  getToken(): string | null {
    return this.token
  }

  getBaseUrl(): string {
    return API_BASE_URL
  }

  async request<T = any>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`
    
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string>),
    }

    // Attach user identity for backend (used by OAuth state and Drive token scoping)
    try {
      const savedUser = localStorage.getItem('auth_user')
      if (savedUser) {
        const u = JSON.parse(savedUser)
        const userIdHeader = (u?.email || u?.id || '').toString()
        if (userIdHeader && !/@example\.com$/i.test(userIdHeader)) {
          headers['x-user-id'] = userIdHeader
          console.log('API Request - User:', userIdHeader, 'Endpoint:', endpoint)
        }
      }
      // If no user header set (unauthenticated), send an ephemeral ID for starting OAuth flows
      if (!('x-user-id' in headers)) {
        headers['x-user-id'] = `anon-${Date.now()}`
      }
    } catch {}

    // Add authorization header if token exists
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`
    }

    // Ensure correct Content-Type handling for FormData vs JSON
    const isFormData = typeof FormData !== 'undefined' && (options.body as any) instanceof FormData
    if (!isFormData) {
      headers['Content-Type'] = headers['Content-Type'] || 'application/json'
    } else if ('Content-Type' in headers) {
      // Let the browser set multipart/form-data boundary automatically
      delete (headers as any)['Content-Type']
    }

    const config: RequestInit = {
      headers,
      ...options,
    }

    try {
      const started = Date.now()
      console.debug('➡️ API Request', { endpoint, url, method: options.method || 'GET' })
      const response = await fetch(url, config)
      const ms = Date.now() - started
      console.debug('⬅️ API Response', { endpoint, status: response.status, ms })
      
      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`)
      }
      
      return await response.json()
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error)
      throw error
    }
  }

  // Assets
  async getAssets(): Promise<Asset[]> {
    return this.request<Asset[]>('/api/assets')
  }

  async uploadAssets(files: FormData): Promise<Asset[]> {
    const response = await this.request<{assets: Asset[]}>('/api/assets/upload', {
      method: 'POST',
      body: files,
      headers: {}, // Don't set Content-Type for FormData
    })
    return response.assets
  }

  async classifyAsset(assetId: string): Promise<Asset> {
    return this.request<Asset>(`/api/assets/${assetId}/classify`, {
      method: 'POST',
    })
  }

  // Campaign file upload
  async uploadCampaignFiles(files: FormData): Promise<{ success: boolean; data?: { assets: Asset[] } }> {
    return this.request('/api/campaigns/upload-files', {
      method: 'POST',
      body: files,
      headers: {}, // Don't set Content-Type for FormData
    })
  }

  // ZIP file upload and extraction
  async uploadZipFile(formData: FormData): Promise<{ success: boolean; data?: { extractedFiles: Asset[] } }> {
    return this.request('/api/campaigns/upload-zip', {
      method: 'POST', 
      body: formData,
      headers: {}, // Don't set Content-Type for FormData
    })
  }

  // Asset AI analysis
  async analyzeAsset(assetId: string): Promise<{ success: boolean; data?: any }> {
    return this.request(`/api/ai-analysis/analyze-asset`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ assetId }),
    })
  }

  // Workflows
  async getWorkflows(): Promise<WorkflowStep[]> {
    return this.request<WorkflowStep[]>('/api/workflows')
  }

  async createWorkflow(data: { name: string; assets: string[] }): Promise<WorkflowStep> {
    return this.request<WorkflowStep>('/api/workflows', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateWorkflow(id: string, data: Partial<WorkflowStep>): Promise<WorkflowStep> {
    return this.request<WorkflowStep>(`/api/workflows/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  // Dashboard
  async getDashboardStats(): Promise<{
    totalAssets: number
    processedToday: number
    activeRules: number
    traffickingJobs: number
  }> {
    return this.request('/api/dashboard/stats')
  }

  // Rules
  async getRules(): Promise<any[]> {
    return this.request('/api/rules')
  }

  async createRule(ruleData: any): Promise<any> {
    return this.request('/api/rules', {
      method: 'POST',
      body: JSON.stringify(ruleData),
    })
  }

  async updateRule(id: string, ruleData: any): Promise<any> {
    return this.request(`/api/rules/${id}`, {
      method: 'PUT',
      body: JSON.stringify(ruleData),
    })
  }

  async testRule(id: string, testData: any): Promise<any> {
    return this.request(`/api/rules/${id}/test`, {
      method: 'POST',
      body: JSON.stringify({ testData }),
    })
  }

  // Trafficking
  async getTraffickingPackages(): Promise<any[]> {
    return this.request('/api/trafficking')
  }

  async createTraffickingPackage(packageData: any): Promise<any> {
    return this.request('/api/trafficking', {
      method: 'POST',
      body: JSON.stringify(packageData),
    })
  }

  async sendTraffickingPackage(id: string): Promise<any> {
    return this.request(`/api/trafficking/${id}/send`, {
      method: 'POST',
    })
  }

  // Campaigns
  async getCampaigns(filters?: { status?: string; customerType?: string; phase?: string }): Promise<any[]> {
    const params = new URLSearchParams()
    if (filters?.status) params.append('status', filters.status)
    if (filters?.customerType) params.append('customerType', filters.customerType)
    if (filters?.phase) params.append('phase', filters.phase)
    
    const queryString = params.toString()
    const endpoint = queryString ? `/api/campaigns?${queryString}` : '/api/campaigns'
    
    const response = await this.request<{ success: boolean; data: any[] }>(endpoint)
    return response.data
  }

  async getCampaign(id: string): Promise<any> {
    const response = await this.request<{ success: boolean; data: any }>(`/api/campaigns/${id}`)
    return response.data
  }

  async createCampaign(campaignData: any): Promise<any> {
    const response = await this.request<{ success: boolean; data: any }>('/api/campaigns', {
      method: 'POST',
      body: JSON.stringify(campaignData),
    })
    return response.data
  }

  async updateCampaign(id: string, campaignData: any): Promise<any> {
    const response = await this.request<{ success: boolean; data: any }>(`/api/campaigns/${id}`, {
      method: 'PUT',
      body: JSON.stringify(campaignData),
    })
    return response.data
  }

  async deleteCampaign(id: string): Promise<any> {
    const response = await this.request<{ success: boolean; data: any }>(`/api/campaigns/${id}`, {
      method: 'DELETE',
    })
    return response.data
  }

  async getCampaignAssets(campaignId: string): Promise<any[]> {
    const response = await this.request<{ success: boolean; data: any[] }>(`/api/campaigns/${campaignId}/assets`)
    return response.data
  }

  async addAssetToCampaign(campaignId: string, assetData: any): Promise<any> {
    const response = await this.request<{ success: boolean; data: any }>(`/api/campaigns/${campaignId}/assets`, {
      method: 'POST',
      body: JSON.stringify(assetData),
    })
    return response.data
  }

  // Google Drive Integration
  async importFromGoogleDrive(importData: {
    fileId: string
    fileName: string
    mimeType: string
    size: string
    downloadUrl: string
    campaignId?: string
  }): Promise<any> {
    const response = await this.request<{ success: boolean; data: any }>('/api/assets/import-from-drive', {
      method: 'POST',
      body: JSON.stringify(importData),
    })
    return response.data
  }

  // Google Drive OAuth2 Integration
  async getGoogleDriveAuthUrl(): Promise<{ success: boolean; data: { authUrl: string; userId: string } }> {
    const response = await this.request<{ success: boolean; data: { authUrl: string; userId: string } }>('/api/google-drive/auth-url')
    return response
  }

  async checkGoogleDriveConnection(): Promise<any> {
    const response = await this.request<{ success: boolean; data: any }>('/api/google-drive/status')
    return response.data
  }

  async getGoogleDriveFiles(params?: { pageSize?: number; q?: string; orderBy?: string; parents?: string }): Promise<any> {
    const queryParams = new URLSearchParams()
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString())
    if (params?.q) queryParams.append('q', params.q)
    if (params?.orderBy) queryParams.append('orderBy', params.orderBy)
    if (params?.parents) queryParams.append('parents', params.parents)
    
    const queryString = queryParams.toString()
    const endpoint = queryString ? `/api/google-drive/files?${queryString}` : '/api/google-drive/files'
    
    const response = await this.request<{ success: boolean; data: any }>(endpoint)
    return response.data
  }

  async revokeGoogleDriveConnection(): Promise<any> {
    const response = await this.request<{ success: boolean; data: any }>('/api/oauth/google-drive/revoke', {
      method: 'POST'
    })
    return response.data
  }

  // Configuration Management
  async getConfigurations(): Promise<any[]> {
    const response = await this.request<{ success: boolean; data: any[] }>('/api/configurations')
    return response.data
  }

  async saveConfiguration(config: { name: string; description: string; fields: any }): Promise<any> {
    const response = await this.request<{ success: boolean; data: any }>('/api/configurations', {
      method: 'POST',
      body: JSON.stringify(config)
    })
    return response.data
  }

  async deleteConfiguration(configId: string): Promise<any> {
    const response = await this.request<{ success: boolean; data: any }>(`/api/configurations/${configId}`, {
      method: 'DELETE'
    })
    return response.data
  }

  async getFieldConfigurations(): Promise<any[]> {
    const response = await this.request<{ success: boolean; data: any[] }>('/api/configurations/fields')
    return response.data
  }

  async updateFieldConfiguration(key: string, config: any): Promise<any> {
    const response = await this.request<{ success: boolean; data: any }>(`/api/configurations/fields/${key}`, {
      method: 'PUT',
      body: JSON.stringify(config)
    })
    return response.data
  }

  async validateConfiguration(formData: any): Promise<{ valid: boolean; errors: any[] }> {
    const response = await this.request<{ success: boolean; data: { valid: boolean; errors: any[] } }>('/api/configurations/validate', {
      method: 'POST',
      body: JSON.stringify({ formData })
    })
    return response.data
  }

  // Authentication
  async login(email: string, password: string): Promise<{ success: boolean; data?: { user: any; token: string }; message?: string }> {
    return this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
  }

  async register(email: string, password: string, firstName: string, lastName: string): Promise<{ success: boolean; data?: { user: any; token: string }; message?: string }> {
    return this.request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, firstName, lastName }),
    })
  }

  async getCurrentUser(): Promise<{ success: boolean; data?: { user: any }; message?: string }> {
    return this.request('/api/auth/me')
  }

  async refreshToken(): Promise<{ success: boolean; data?: { token: string }; message?: string }> {
    return this.request('/api/auth/refresh', {
      method: 'POST',
    })
  }

  // Health check
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    return this.request('/health')
  }

  // Settings and credentials
  async getUserSettings(): Promise<{ success: boolean; data: { settings: Record<string, any> } }> {
    return this.request('/api/settings')
  }

  async saveUserSetting(key: string, value: any): Promise<{ success: boolean; data: any }> {
    return this.request(`/api/settings/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    })
  }

  async saveUserSettings(settings: Record<string, any>): Promise<{ success: boolean; data: any }> {
    return this.request('/api/settings', {
      method: 'POST',
      body: JSON.stringify({ settings }),
    })
  }

  async getCredentialStatus(): Promise<{ success: boolean; data: { status: Record<string, { connected: boolean; hasCredentials: boolean }> } }> {
    return this.request('/api/settings/credentials-status')
  }

  async saveServiceCredentials(serviceName: string, credentialType: string, credentials: any): Promise<{ success: boolean; data: any }> {
    return this.request('/api/settings/credentials', {
      method: 'POST',
      body: JSON.stringify({
        serviceName,
        credentialType,
        credentials
      }),
    })
  }

  async getServiceCredentials(serviceName: string, credentialType: string): Promise<{ success: boolean; data: { credentials: any } }> {
    return this.request(`/api/settings/credentials/${serviceName}/${credentialType}`)
  }

  async deleteServiceCredentials(serviceName: string, credentialType: string): Promise<{ success: boolean; data: any }> {
    return this.request(`/api/settings/credentials/${serviceName}/${credentialType}`, {
      method: 'DELETE',
    })
  }
}

export const apiService = new ApiService()